import { Button, Spin, Tooltip } from "antd";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import OverviewStats from "./OverviewStats";
import moment from "moment";
import { HistoryOutlined, WarningOutlined } from "@ant-design/icons";
import AddTopPlanModal from "../modal/add/AddTopPlanModal";
import ConfirmationModal from "../modal/ConfirmationModal";
import ActivePerfCycleApi from "../../api/ActivePerfCycleApi";
import CompletePerfCycleApi from "../../api/CompletePerfCycleApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { perfCycleDetails } from "../../features/PerfCycleSlice";
import GetPerfCycleApi from "../../api/GetPerfCycleApi";
import PerfCycleTimelineModal from "../modal/info/PerfCycleTimelineModal";
import { permissionAbility } from "../../helpers/permissionHelpers";
import StockMarketWidget from "./StockMarketWidget";
import GoogleNewsWidget from "./GoogleNewsWidget";
import CombinedPerformanceCarousel from "./CombinedPerformanceCarousel";
import BestWorstTopPlans from "./BestWorstTopPlans";
import QuickActions from "./QuickActions";
import RecentActivities from "./RecentActivities";
import AttentionRequired from "./AttentionRequired";
import ExecutiveStethoscope from "./ExecutiveStethoscope";
import NeedsAttentionToday from "./NeedsAttentionToday";
import EmployeePerformanceSummary from "./EmployeePerformanceSummary";
import AIExecutiveAdvisor from "./AIExecutiveAdvisor";
import Theme from "../../theme/Theme";

function CurrentCycle() {
  const [addModal, setAddModal] = useState(false);
  const [timelineModal, setTimelineModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmType, setConfirmType] = useState("");
  const [widgetPreferences, setWidgetPreferences] = useState({
    stockMarket: true,
    googleNews: true,
    attentionRequired: true,
    needsAttentionToday: true,
    employeePerformanceSummary: true,
    quickActions: true,
    recentActivities: true,
  });

  const [planAdd, setPlanAdd] = useState(false);
  const userState = useSelector((state) => state.userState.value);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);

  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;
  const proHeight = document.body.clientHeight;
  const dispatch = useDispatch();

  // Load widget preferences from localStorage
  useEffect(() => {
    const savedPreferences = localStorage.getItem("widgetPreferences");
    if (savedPreferences) {
      try {
        setWidgetPreferences(JSON.parse(savedPreferences));
      } catch (error) {
        console.error("Error loading widget preferences:", error);
      }
    }
  }, []);

  async function changeStatus() {
    let apiResponse = {};
    if (confirmType === "activate") {
      apiResponse = await ActivePerfCycleApi(
        perfCycleState.id,
        positionState?.id
      );
    } else if (confirmType === "complete") {
      apiResponse = await CompletePerfCycleApi(
        perfCycleState.id,
        positionState?.id
      );
    } else if (confirmType === "reactivate") {
      apiResponse = await ActivePerfCycleApi(
        perfCycleState.id,
        positionState?.id
      );
    }

    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", `Perf cycle ${confirmType}ed`);
      /*As value is object we had to empty it to rerender */
      dispatch(perfCycleDetails({}));
      dispatch(perfCycleDetails(apiResponse.performanceCycle));
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't complete. Try again later."
      );
    }
    setConfirmType("");
  }

  function getTimingText() {
    let output = "";
    if (moment(perfCycleState?.startingOn) > moment()) {
      output = `This performance cycle starts ${moment(
        perfCycleState?.startingOn
      )
        .endOf("day")
        .fromNow()}`;
    } else if (moment(perfCycleState?.endingOn) < moment()) {
      output = `This performance cycle ended ${moment(perfCycleState?.endingOn)
        .endOf("day")
        .fromNow()}`;
    } else {
      output = `This performance cycle ends ${moment(perfCycleState?.endingOn)
        .endOf("day")
        .fromNow()}`;
    }

    return output;
  }

  async function planAdded() {
    if (!perfCycleState.havePlans) {
      setLoading(true);
      let apiResponse = await GetPerfCycleApi(perfCycleState.id);
      if (apiResponse.message === "success") {
        /*As value is object we had to empty it to rerender */
        dispatch(perfCycleDetails({}));
        dispatch(perfCycleDetails(apiResponse.performanceCycle));
      }
      setLoading(false);
    }
    /*To rerender overview stats */
    setPlanAdd(!planAdd);
  }

  return (
    <>
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div 
            className="text-2xl mb-3" 
            style={{ 
              color: Theme().textPrimary,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
            }}
          >
            Welcome, {userState?.firstName}
          </div>
        </div>
        <div className="inline">
          {permissionAbility(userLevel, "perf-cycles", "manage") && (
            <div className="flex items-center">
              {perfCycleState?.status === "Active" ? (
                <Button
                  danger={moment(perfCycleState?.endingOn) < moment(new Date())}
                  onClick={() => {
                    setConfirmType("complete");
                    setConfirmModal(true);
                  }}
                >
                  Complete
                </Button>
              ) : perfCycleState?.status === "Scheduled" ? (
                <Button
                  type={
                    moment(perfCycleState?.startingOn) < moment(new Date()) &&
                    "primary"
                  }
                  onClick={() => {
                    setConfirmType("activate");
                    setConfirmModal(true);
                  }}
                >
                  Activate
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    setConfirmType("reactivate");
                    setConfirmModal(true);
                  }}
                >
                  Reactivate
                </Button>
              )}
              <Tooltip title={"Timeline"}>
                <div
                  onClick={() => setTimelineModal(true)}
                  className="ml-2 cursor-pointer"
                  style={{ color: Theme().primary }}
                >
                  <HistoryOutlined />
                </div>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      {perfCycleState?.havePlans ? (
        <>
          <CombinedPerformanceCarousel />

          {/* AI Executive Strategy Advisor */}
          <AIExecutiveAdvisor />

          {/* Executive Analytics Stethoscope */}
          <ExecutiveStethoscope />
          
          {/* Attention Required Section */}
          {widgetPreferences.attentionRequired && <AttentionRequired />}

          {/* Needs Your Attention Today */}
          {widgetPreferences.needsAttentionToday !== false && <NeedsAttentionToday />}

          {/* Employee Performance Summary */}
          {widgetPreferences.employeePerformanceSummary !== false && <EmployeePerformanceSummary />}

          {/* Two column layout: Quick Actions + Recent Activities on left, Google News on right */}
          <div className="grid grid-cols-10 gap-5 mb-10">
            {/* Left side - Quick Actions and Recent Activities */}
            {(widgetPreferences.quickActions || widgetPreferences.recentActivities) && (
              <div className={widgetPreferences.googleNews ? "col-span-6 space-y-6" : "col-span-10 space-y-6"}>
                {widgetPreferences.quickActions && <QuickActions />}
                {widgetPreferences.recentActivities && <RecentActivities />}
              </div>
            )}
            
            {/* Right side - Google News */}
            {widgetPreferences.googleNews && (
              <div className={(widgetPreferences.quickActions || widgetPreferences.recentActivities) ? "col-span-4" : "col-span-10"}>
                <GoogleNewsWidget />
              </div>
            )}
          </div>
          
          {widgetPreferences.stockMarket && (
            <div className="mb-10">
              <StockMarketWidget />
            </div>
          )}
          
          {/* Hidden: Best and Worst Performing Plans */}
          {/* <div className="mt-10">
            <BestWorstTopPlans />
          </div> */}
        </>
      ) : (
        <Spin spinning={loading}>
          <div
            style={{ height: proHeight - 180 }}
            className={"flex w-full items-center justify-center"}
          >
            {perfCycleState?.status != "Completed" ? (
              <div className="text-center">
                <div className="text-3xl text-gray-400">
                  <WarningOutlined />
                </div>
                <div className="text-lg pri-font text-gray-400">
                  No plans available
                </div>
                <div className="mb-3 text-gray-400 text-sm">
                  Add plans to performance cycle
                </div>
                <Button onClick={() => setAddModal(true)} type="dashed">
                  Add plans
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <div className="text-3xl text-gray-400">
                  <WarningOutlined />
                </div>
                <div className="text-lg pri-font text-gray-400">
                  This performance cycle is ended
                </div>
              </div>
            )}
          </div>
        </Spin>
      )}

      <AddTopPlanModal
        visible={addModal}
        handleCancel={() => setAddModal(false)}
        handleOk={() => planAdded()}
      />
      <ConfirmationModal
        visible={confirmModal}
        handleCancel={() => setConfirmModal(false)}
        handleOk={() => {
          changeStatus();
          setConfirmModal(false);
        }}
        text={`Are you sure want to ${confirmType} this cycle?`}
        title={`${
          confirmType.charAt(0).toUpperCase() + confirmType.slice(1)
        } cycle`}
      />
      <PerfCycleTimelineModal
        visible={timelineModal}
        handleCancel={() => setTimelineModal(false)}
      />
    </>
  );
}

export default CurrentCycle;
